'use client';

import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { ROUTES } from '../../constants/routes';
import { CLOSE_PUBLIC_MOBILE_NAV_EVENT } from '../../lib/publicMobileNavEvents';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/icon-button';
import { NavLink } from '../ui/NavLink';
import { themeConfig } from '../../theme';
import { cn } from '../../lib/utils';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../../features/auth/hooks/useAuthSession';
import { useSignOut } from '../../features/auth/hooks/useSignOut';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive
      ? 'bg-accent text-accent-foreground shadow-sm'
      : 'text-header-muted hover:bg-accent/50 hover:text-header-foreground',
  );

const navItems = [
  { to: ROUTES.adoptShelterQuizRoot, label: 'Shelters', end: true as const },
  { to: ROUTES.matchingPoc, label: 'Match (KNN POC)', end: true as const },
] as const;

/** Public site header only; `/admin` workspace uses `AppShellLayout` + `AppToolbar`. */
export function MainNav() {
  const [open, setOpen] = useState(false);
  const { data: session } = useAuthSession();
  const authed = isAuthenticatedSession(session);
  const signOut = useSignOut();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    const onOverlay = () => setOpen(false);
    window.addEventListener(CLOSE_PUBLIC_MOBILE_NAV_EVENT, onOverlay);
    return () => window.removeEventListener(CLOSE_PUBLIC_MOBILE_NAV_EVENT, onOverlay);
  }, []);

  return (
    <header className='sticky top-0 z-40 border-b border-header-foreground/15 bg-header/95 text-header-foreground backdrop-blur supports-backdrop-filter:bg-header/90'>
      <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8'>
        <NextLink
          href={ROUTES.home}
          className='flex min-w-0 items-center gap-2 text-header-foreground no-underline'
        >
          <img
            src={themeConfig.logos.icon}
            alt=''
            width={36}
            height={36}
            className='size-9 shrink-0 rounded-lg'
          />
          <span className='font-heading text-lg font-semibold tracking-tight'>
            {themeConfig.brand.shortName}
          </span>
        </NextLink>

        <nav
          className='hidden items-center gap-1 md:flex'
          aria-label='Main navigation'
        >
          {navItems.map(({ to, label, end }) => (
            <NavLink
              key={to}
              href={to}
              className={navLinkClass}
              end={end}
            >
              {label}
            </NavLink>
          ))}
          {authed ? (
            <>
              <NavLink href={ROUTES.admin} className={navLinkClass}>
                Admin
              </NavLink>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={signOut}
                className='h-auto min-h-0 rounded-md px-3 py-2 text-sm font-medium text-header-muted hover:bg-accent/50 hover:text-header-foreground'
              >
                Sign out
              </Button>
            </>
          ) : (
            <NavLink href={ROUTES.auth} className={navLinkClass}>
              Sign in
            </NavLink>
          )}
        </nav>

        <IconButton
          type='button'
          variant='ghost'
          className='rounded-lg p-2 text-header-foreground md:hidden [&_svg]:size-7'
          aria-expanded={open}
          aria-controls={open ? 'mobile-nav-panel' : undefined}
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <Icon name='close' className='size-7' />
          ) : (
            <Icon name='menu-burger' className='size-7' />
          )}
        </IconButton>
      </div>

      {open ? (
        <div
          id='mobile-nav-panel'
          className='fixed inset-0 z-50 md:hidden'
          role='dialog'
          aria-modal='true'
          aria-label='Site menu'
        >
          <Button
            type='button'
            variant='ghost'
            className='absolute inset-0 h-full min-h-0 w-full rounded-none border-0 bg-foreground/50 p-0 shadow-none hover:bg-foreground/55'
            aria-label='Close menu'
            onClick={() => setOpen(false)}
          />
          <div className='absolute right-0 top-0 flex h-full w-[min(100%,20rem)] flex-col border-l border-border bg-card text-card-foreground shadow-xl'>
            <div className='flex h-16 shrink-0 items-center justify-between border-b border-border px-4'>
              <span className='font-heading text-sm font-semibold'>Menu</span>
              <IconButton
                type='button'
                variant='ghost'
                className='rounded-lg text-muted-foreground hover:text-foreground [&_svg]:size-6'
                aria-label='Close menu'
                onClick={() => setOpen(false)}
              >
                <Icon name='close' className='size-6' />
              </IconButton>
            </div>
            <nav
              className='flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-y-contain p-3'
              aria-label='Mobile navigation'
            >
              {navItems.map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  href={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'rounded-lg px-3 py-3 text-base font-medium',
                      isActive
                        ? 'bg-primary-600 text-primary-foreground'
                        : 'text-foreground hover:bg-accent',
                    )
                  }
                  onClick={() => setOpen(false)}
                >
                  {label}
                </NavLink>
              ))}
              {authed ? (
                <>
                  <NavLink
                    href={ROUTES.admin}
                    className='rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-accent'
                    onClick={() => setOpen(false)}
                  >
                    Admin
                  </NavLink>
                  <Button
                    type='button'
                    variant='ghost'
                    className='h-auto min-h-0 w-full justify-start rounded-lg px-3 py-3 text-left text-base font-medium text-foreground hover:bg-accent'
                    onClick={() => {
                      setOpen(false);
                      signOut();
                    }}
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <NavLink
                  href={ROUTES.auth}
                  className='rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-accent'
                  onClick={() => setOpen(false)}
                >
                  Sign in
                </NavLink>
              )}
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
}
